import { phoneMasks } from "./PhoneMasks";

const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;

export type PhoneMaskEntry = {
  title: string;
  value: string;
  iso2: string;
  mask: string;
};

export type PhoneInput =
  | string
  | {
      id?: string;
      prefix?: string | null;
      number?: string;
      phone?: string;
    }
  | null
  | undefined;

/** Normalized phone produced by this module (map to app types at the boundary). */
export type ParsedPhone = {
  id?: string;
  prefix: string | null;
  number: string;
};

/** Keeps only `+` and ASCII digits. */
export function sanitizePhoneChars(value: string): string {
  return value.replace(/[^\d+]/g, "");
}

export function joinPhonePrefixAndNumber(prefix: string, number: string): string {
  return sanitizePhoneChars(prefix) + sanitizePhoneChars(number);
}

/** Count of `#` digit placeholders in a mask. */
export function maskDigitCount(mask: string): number {
  let count = 0;
  for (const char of mask) {
    if (char === "#") count++;
  }
  return count;
}

export function escapeRegexLiteral(char: string): string {
  return char.replace(REGEX_ESCAPE_PATTERN, "\\$&");
}

/** `^\d{N}$` — validates a digit-only subscriber number against the mask length. */
export function maskToLengthRegexSource(mask: string): string {
  return `^\\d{${maskDigitCount(mask)}}$`;
}

export function maskToLengthRegex(mask: string): RegExp {
  return new RegExp(maskToLengthRegexSource(mask));
}

/**
 * Format-aware pattern: `#` → `\d`, all other mask characters are literal
 * (parentheses, dashes, spaces, and fixed digits like the leading `9` in Argentina).
 */
export function maskToFormatRegexSource(mask: string): string {
  let pattern = "^";
  for (const char of mask) {
    pattern += char === "#" ? "\\d" : escapeRegexLiteral(char);
  }
  pattern += "$";
  return pattern;
}

export function maskToFormatRegex(mask: string): RegExp {
  return new RegExp(maskToFormatRegexSource(mask));
}

export type PhoneMaskRegexes = {
  digitCount: number;
  lengthRegex: RegExp;
  lengthRegexSource: string;
  formatRegex: RegExp;
  formatRegexSource: string;
};

export function phoneMaskToRegexes(mask: string): PhoneMaskRegexes {
  return {
    digitCount: maskDigitCount(mask),
    lengthRegex: maskToLengthRegex(mask),
    lengthRegexSource: maskToLengthRegexSource(mask),
    formatRegex: maskToFormatRegex(mask),
    formatRegexSource: maskToFormatRegexSource(mask),
  };
}

/** Applies a phone mask to a digit-only string (same rules as the Storybook helper). */
export function formatPhoneWithMask(digits: string, mask: string): string {
  const sanitized = sanitizePhoneChars(digits).replace(/\+/g, "");
  let result = "";
  let digitIndex = 0;

  for (let i = 0; i < mask.length && digitIndex < sanitized.length; i++) {
    if (mask[i] === "#") {
      result += sanitized[digitIndex];
      digitIndex++;
    } else {
      result += mask[i];
    }
  }

  return result;
}

export function phoneNumberDigitsOnly(number: string): string {
  return sanitizePhoneChars(number).replace(/\+/g, "");
}

export function phoneMatchesMaskLength(number: string, mask: string): boolean {
  return maskToLengthRegex(mask).test(phoneNumberDigitsOnly(number));
}

export function phoneMatchesMaskFormat(number: string, mask: string): boolean {
  const digits = phoneNumberDigitsOnly(number);
  return maskToFormatRegex(mask).test(formatPhoneWithMask(digits, mask));
}

export type ResolvedPhoneMask = {
  maskEntry: PhoneMaskEntry;
  maskKey: string;
  /** Digits in `prefix` after the matched country/region calling code (e.g. `21` for `+121`). */
  nationalPrefixDigits: string;
};

/** Longest `phoneMasks` key whose sanitized prefix matches the start of `prefix`. */
export function resolvePhoneMask(prefix: string): ResolvedPhoneMask | undefined {
  const sanitizedPrefix = sanitizePhoneChars(prefix);
  if (!sanitizedPrefix) {
    return undefined;
  }

  const exactKey = Object.keys(phoneMasks).find((key) => key === prefix);
  if (exactKey) {
    const sanitizedKey = sanitizePhoneChars(exactKey);
    return {
      maskEntry: phoneMasks[exactKey as keyof typeof phoneMasks],
      maskKey: exactKey,
      nationalPrefixDigits: sanitizedPrefix.slice(sanitizedKey.length),
    };
  }

  const keys = Object.keys(phoneMasks).sort(
    (a, b) => sanitizePhoneChars(b).length - sanitizePhoneChars(a).length
  );

  for (const key of keys) {
    const sanitizedKey = sanitizePhoneChars(key);
    if (sanitizedPrefix.startsWith(sanitizedKey)) {
      return {
        maskEntry: phoneMasks[key as keyof typeof phoneMasks],
        maskKey: key,
        nationalPrefixDigits: sanitizedPrefix.slice(sanitizedKey.length),
      };
    }
  }

  return undefined;
}

export function findPhoneMaskForPrefix(prefix: string): PhoneMaskEntry | undefined {
  return resolvePhoneMask(prefix)?.maskEntry;
}

/** National subscriber digits: trailing prefix digits + local number (for mask validation/formatting). */
export function buildSubscriberDigits(prefix: string, number: string): string {
  const resolved = resolvePhoneMask(prefix);
  const numberDigits = phoneNumberDigitsOnly(number);
  if (!resolved) {
    return numberDigits;
  }
  return resolved.nationalPrefixDigits + numberDigits;
}

export function phoneMatchesMaskLengthForPrefix(
  prefix: string,
  number: string,
  mask: string
): boolean {
  return maskToLengthRegex(mask).test(buildSubscriberDigits(prefix, number));
}

export function phoneMatchesMaskFormatForPrefix(
  prefix: string,
  number: string,
  mask: string
): boolean {
  const subscriberDigits = buildSubscriberDigits(prefix, number);
  return maskToFormatRegex(mask).test(formatPhoneWithMask(subscriberDigits, mask));
}

export type PhoneMaskAnalysis = {
  joined: string;
  prefixSanitized: string;
  numberSanitized: string;
  nationalPrefixDigits: string;
  subscriberDigits: string;
  maskEntry: PhoneMaskEntry | undefined;
  mask: string | undefined;
  formatted: string | undefined;
  regexes: PhoneMaskRegexes | undefined;
  matchesLength: boolean;
  matchesFormat: boolean;
};

/**
 * Normalizes raw phone input: country `prefix` from the mask table and `number`
 * formatted per mask (national digits embedded in the prefix are merged in).
 */
export function parsePhone(input: PhoneInput): ParsedPhone {
  if (typeof input === "string") {
    return { prefix: null, number: input };
  }

  if (!input || typeof input !== "object") {
    return { prefix: null, number: "" };
  }

  const prefix = input.prefix ?? null;
  const rawNumber = input.number || input.phone || "";

  if (!prefix) {
    return {
      id: input.id,
      prefix: null,
      number: rawNumber,
    };
  }

  const resolved = resolvePhoneMask(prefix);
  if (!resolved) {
    return {
      id: input.id,
      prefix,
      number: rawNumber,
    };
  }

  const subscriberDigits = buildSubscriberDigits(prefix, rawNumber);

  return {
    id: input.id,
    prefix: resolved.maskEntry.value,
    number: formatPhoneWithMask(subscriberDigits, resolved.maskEntry.mask),
  };
}

export function analyzePhone(prefix: string, number: string): PhoneMaskAnalysis {
  const prefixSanitized = sanitizePhoneChars(prefix);
  const numberSanitized = phoneNumberDigitsOnly(number);
  const joined = prefixSanitized + numberSanitized;
  const resolved = resolvePhoneMask(prefix);
  const maskEntry = resolved?.maskEntry;
  const nationalPrefixDigits = resolved?.nationalPrefixDigits ?? "";
  const subscriberDigits = buildSubscriberDigits(prefix, number);
  const mask = maskEntry?.mask;
  const regexes = mask ? phoneMaskToRegexes(mask) : undefined;
  const formatted = mask ? formatPhoneWithMask(subscriberDigits, mask) : undefined;

  return {
    joined,
    prefixSanitized,
    numberSanitized,
    nationalPrefixDigits,
    subscriberDigits,
    maskEntry,
    mask,
    formatted,
    regexes,
    matchesLength: mask ? phoneMatchesMaskLengthForPrefix(prefix, number, mask) : false,
    matchesFormat: mask ? phoneMatchesMaskFormatForPrefix(prefix, number, mask) : false,
  };
}

/** ISO 3166-1 alpha-2 region from a BCP 47 locale tag (e.g. `"fr-FR"` → `"FR"`). */
export function regionFromLocale(locale: string): string | null {
  try {
    return new Intl.Locale(locale).region ?? null;
  } catch {
    return null;
  }
}

/** Maps an ISO2 country code to a `phoneMasks` prefix value (e.g. `"FR"` → `"+33"`). */
export function prefixFromIso2(iso2: string): string | null {
  const normalized = iso2.toUpperCase();
  const entry = Object.values(phoneMasks).find(
    (mask) => mask.iso2 === normalized || mask.iso2.split("/").includes(normalized)
  );
  return entry?.value ?? null;
}

/** Maps an ISO2 address / tax-id country code to a calling prefix. */
export function prefixFromAddressCountry(country: string | null | undefined): string | null {
  if (!country?.trim()) return null;
  return prefixFromIso2(country.trim());
}

export type DefaultPhonePrefixSources = {
  practicePhonePrefix?: string | null;
  practiceAddressCountry?: string | null;
  companyPhonePrefix?: string | null;
  companyAddressCountry?: string | null;
  companyCountry?: string | null;
  staffPhonePrefix?: string | null;
  browserLocale?: string;
};

export type DefaultPhonePrefixSource =
  | "practicePhone"
  | "practiceCountry"
  | "companyPhone"
  | "companyCountry"
  | "companyAddressCountry"
  | "staffPhone"
  | "browserLocale";

export type ResolvedDefaultPhonePrefix = {
  prefix: string;
  source: DefaultPhonePrefixSource;
};

/**
 * Resolves the best default calling prefix from business context, then browser locale.
 * Priority: practice phone → practice country → company phone → company country → staff phone → locale.
 */
export function resolveDefaultPhonePrefix(
  sources: DefaultPhonePrefixSources
): ResolvedDefaultPhonePrefix | null {
  const candidates: Array<{ source: DefaultPhonePrefixSource; prefix: string | null }> = [
    { source: "practicePhone", prefix: sources.practicePhonePrefix ?? null },
    {
      source: "practiceCountry",
      prefix: prefixFromAddressCountry(sources.practiceAddressCountry),
    },
    { source: "companyPhone", prefix: sources.companyPhonePrefix ?? null },
    { source: "companyCountry", prefix: prefixFromAddressCountry(sources.companyCountry) },
    {
      source: "companyAddressCountry",
      prefix: prefixFromAddressCountry(sources.companyAddressCountry),
    },
    { source: "staffPhone", prefix: sources.staffPhonePrefix ?? null },
    { source: "browserLocale", prefix: guessDefaultPhonePrefix(sources.browserLocale) },
  ];

  for (const candidate of candidates) {
    if (candidate.prefix) {
      return { prefix: candidate.prefix, source: candidate.source };
    }
  }

  return null;
}

/** Whether locale-based phone prefix auto-detection should run in the UI. */
export function isPhonePrefixAutoDetectEnabled(): boolean {
  if (import.meta.env.VITE_DISABLE_PHONE_PREFIX_AUTODETECT === "true") {
    return false;
  }
  // Vitest (unit + Storybook interaction tests) run with MODE=test.
  if (import.meta.env.MODE === "test") {
    return false;
  }
  return true;
}

/**
 * Best-effort default calling prefix from browser/app locale.
 * Falls back through explicit locale, `navigator.language`, then `document.documentElement.lang`.
 */
export function guessDefaultPhonePrefix(locale?: string): string | null {
  const candidates = [
    locale,
    typeof navigator !== "undefined" ? navigator.language : undefined,
    typeof document !== "undefined" ? document.documentElement.lang : undefined,
  ].filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    const region = regionFromLocale(candidate);
    if (region) {
      const prefix = prefixFromIso2(region);
      if (prefix) return prefix;
    }
  }

  return null;
}
